import { ref, watch, onUnmounted } from 'vue'

/**
 * Composable that manages click-outside detection for menus/popups.
 * Returns a reactive `isOpen` ref and a template ref for the container element.
 */
export function useClickOutside() {
  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  let pendingTimer: ReturnType<typeof setTimeout> | null = null

  function onClickOutside(e: MouseEvent) {
    if (containerRef.value && !containerRef.value.contains(e.target as Node)) {
      isOpen.value = false
    }
  }

  watch(isOpen, (open) => {
    if (pendingTimer) {
      clearTimeout(pendingTimer)
      pendingTimer = null
    }
    if (open) {
      pendingTimer = setTimeout(() => {
        pendingTimer = null
        document.addEventListener('click', onClickOutside)
      }, 0)
    } else {
      document.removeEventListener('click', onClickOutside)
    }
  })

  onUnmounted(() => {
    if (pendingTimer) clearTimeout(pendingTimer)
    document.removeEventListener('click', onClickOutside)
  })

  return { isOpen, containerRef }
}
